import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, FileText, Trash2, RefreshCcw,
  FolderOpen, AlertTriangle, CheckCircle2, Clock, Cloud, DownloadCloud, Sparkles,
  Share2, Copy, Mail,
} from "lucide-react";
import { RetentionBanner, LocalVaultCard } from "@/components/LocalVault";
import { downloadToClientVault, getRootHandle } from "@/lib/localVault";

const CATEGORIES = [
  ["INITIAL_PETITION", "Petição inicial"],
  ["DEFENSE",          "Contestação"],
  ["REPLY",            "Réplica"],
  ["SENTENCE",         "Sentença"],
  ["SENTENCE_ED",      "ED de sentença"],
  ["TRT_APPEAL",       "Recurso ordinário"],
  ["TRT_JUDGMENT",     "Acórdão do TRT"],
  ["TRT_ED",           "ED do TRT"],
  ["TST_DECISION",     "Decisão do TST"],
  ["TST_JUDGMENT",     "Acórdão do TST"],
  ["TST_ED",           "ED do TST"],
  ["EXECUTION_DECISION","Decisão de execução"],
  ["SETTLEMENT",       "Acordo/homologação"],
  ["PAYSLIP",          "Holerite/contracheque"],
  ["FINANCIAL_STATEMENT","Ficha financeira"],
  ["TIMECARD",         "Cartão de ponto"],
  ["TRCT",             "TRCT"],
  ["CCT",              "CCT / ACT (norma coletiva)"],
  ["PJE_CALC_REPORT",  "Relatório PJe-Calc"],
  ["EXPERT_CALCULATION","Cálculo do perito"],
  ["PARTY_CALCULATION","Cálculo da parte contrária"],
  ["PAYMENT_RECEIPT",  "Comprovante de pagamento"],
  ["OTHER",            "Outro"],
];
const CATEGORY_LABEL = Object.fromEntries(CATEGORIES);

export default function LiquidacaoDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [retention, setRetention] = useState(null);
  const [hasVault, setHasVault] = useState(false);
  const [lastCalc, setLastCalc] = useState(null);
  const dragCounter = useRef(0);
  const [dragOver, setDragOver] = useState(false);

  const load = useCallback(async () => {
    const [c, d, h] = await Promise.all([
      api.get(`/liq/cases/${id}`),
      api.get(`/liq/cases/${id}/documents`),
      api.get(`/liq/cases/${id}/calculations`).catch(() => ({data: {rows: []}})),
    ]);
    setCaseData(c.data);
    setDocs(d.data.rows || []);
    setLastCalc((h.data.rows || [])[0] || null);
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/liq/retention/policy").then(r => setRetention(r.data)).catch(() => {});
    getRootHandle().then(h => setHasVault(!!h)).catch(() => {});
  }, []);

  // Autosave dos metadados básicos (Seção 5)
  const saveTimer = useRef(null);
  const patch = (patchObj) => {
    setCaseData(prev => ({ ...prev, ...patchObj }));
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        const r = await api.patch(`/liq/cases/${id}`, patchObj);
        setCaseData(r.data);
        setSavedAt(new Date());
      } catch (e) {
        toast.error("Autosave falhou");
      }
    }, 450);
  };

  const upload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const fd = new FormData();
    for (const f of fileList) fd.append("files", f);
    setUploading(true);
    try {
      const r = await api.post(`/liq/cases/${id}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data",
                    "Idempotency-Key": crypto.randomUUID() },
      });
      const { accepted = [], duplicates = [], rejected = [] } = r.data;
      if (accepted.length) toast.success(`${accepted.length} arquivo(s) enviados.`);
      if (duplicates.length) toast.info(`${duplicates.length} duplicado(s) ignorados.`);
      if (rejected.length) toast.error(`${rejected.length} rejeitado(s): ${rejected.map(x => x.reason).slice(0,2).join("; ")}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar arquivos");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Remover este documento?")) return;
    try {
      await api.delete(`/liq/documents/${docId}`);
      toast.success("Documento removido.");
      await load();
    } catch { toast.error("Falha ao remover."); }
  };

  const downloadToVault = async (doc) => {
    try {
      const token = (document.cookie.match(/session_token=([^;]+)/) || [])[1];
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const client = caseData?.claimant?.name || caseData?.process_number || "Cliente";
      const r = await downloadToClientVault({
        url: `${API}/liq/documents/${doc.id}/original`,
        headers,
        client,
        subfolder: "Originais",
        filename: doc.original_filename,
      });
      if (r.mode === "vault") {
        toast.success(`Salvo em Hermes/${r.path}`);
        // Purge no servidor após download — usuário já tem local
        try {
          await api.post(`/liq/documents/${doc.id}/purge`);
          await load();
        } catch { /* purge é opcional */ }
      } else {
        toast.info("Baixado para a pasta Downloads (configure o cofre local no /perfil para gravação automática por cliente).");
      }
    } catch (e) {
      toast.error(e?.message || "Falha ao baixar");
    }
  };

  const reclassify = async (docId, category) => {
    try {
      await api.post(`/liq/documents/${docId}/reclassify`, { category });
      toast.success("Categoria atualizada.");
      await load();
    } catch { toast.error("Falha ao reclassificar."); }
  };

  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragOver(false);
    if (e.dataTransfer?.files) upload(e.dataTransfer.files);
  };
  const onDragEnter = (e) => {
    e.preventDefault(); dragCounter.current++; setDragOver(true);
  };
  const onDragLeave = (e) => {
    e.preventDefault(); dragCounter.current--;
    if (dragCounter.current <= 0) setDragOver(false);
  };

  if (loading) return <div className="p-8 text-slate-500" data-testid="liq-detail-loading">Carregando…</div>;
  if (!caseData) return <div className="p-8 text-rose-600">Liquidação não encontrada.</div>;

  const prog = caseData.progress || {};

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-6" data-testid="liq-detail">
      <div className="max-w-6xl mx-auto space-y-4">
        <RetentionBanner retention={retention} />
        {!hasVault && <LocalVaultCard compact />}

        <div className="flex items-center justify-between">
          <Link to="/calculos/liquidacao-trabalhista"
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
            data-testid="liq-detail-back">
            <ArrowLeft className="w-3.5 h-3.5"/> Voltar às liquidações
          </Link>
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1"
               data-testid="liq-detail-savedat">
            <Cloud className="w-3.5 h-3.5"/>
            {savedAt
              ? `Salvo automaticamente às ${savedAt.toLocaleTimeString("pt-BR").slice(0,5)}`
              : "Autosave ativo"}
          </div>
        </div>

        {/* Header */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="font-mono text-lg font-semibold text-slate-900" data-testid="liq-detail-process">
            {caseData.process_number}
          </div>
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 uppercase tracking-wide font-semibold"
                data-testid="liq-detail-state">
            {caseData.state}
          </span>
        </div>
        <div className="mt-1 text-sm text-slate-600" data-testid="liq-detail-parties">
          <b>{caseData.claimant?.name || "—"}</b>
          <span className="mx-1.5 text-slate-400">×</span>
          {caseData.defendant?.name || "—"}
          <span className="mx-2 text-slate-300">|</span>
          {caseData.court || "—"} {caseData.uf && `· ${caseData.uf}`}
        </div>

        {/* Progresso */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Kpi label="Documentos" value={prog.documents_uploaded ?? 0} testid="kpi-docs"/>
          <Kpi label="Processados" value={prog.documents_processed ?? 0} testid="kpi-processed"/>
          <Kpi label="Parâmetros" value={prog.parameters_extracted ?? 0} testid="kpi-params"/>
          <Kpi label="Confirmados" value={prog.parameters_confirmed ?? 0} tone="ok" testid="kpi-confirmed"/>
          <Kpi label="Pendentes"   value={prog.parameters_pending   ?? 0} tone={prog.parameters_pending ? "warn" : ""} testid="kpi-pending"/>
        </div>

        {/* Dashboard da última liquidação (Fase 18) */}
        {lastCalc && (
          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-5"
               data-testid="liq-detail-last-calc">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-700"/>
              <div className="text-[10px] uppercase tracking-widest text-emerald-800">
                Última liquidação calculada
              </div>
              <span className="text-[10px] text-slate-500">
                {new Date(lastCalc.generated_at).toLocaleString("pt-BR")}
              </span>
              <a
                href={`${API}/liq/calculations/${lastCalc.id}/pdf`}
                target="_blank" rel="noopener"
                className="ml-auto text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-100"
                data-testid="liq-detail-last-calc-pdf"
              >
                <FileText className="w-3.5 h-3.5"/> Ver memória em PDF
              </a>
              <a
                href={`${API}/liq/calculations/${lastCalc.id}/export/pjc`}
                className="text-xs text-slate-700 hover:text-slate-900 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-white border border-slate-200 hover:bg-slate-100"
                data-testid="liq-detail-last-calc-pjc"
                title="Exporta em formato PJC (compatível com PJe-Calc)"
              >
                <DownloadCloud className="w-3.5 h-3.5"/> Baixar .pjc
              </a>
              {lastCalc.public_token && (
                <WhatsAppShareButton
                  token={lastCalc.public_token}
                  totalCents={lastCalc.total_updated_cents || lastCalc.total_cents}
                  processNumber={caseData?.process_number}
                  claimantName={caseData?.claimant?.name}
                />
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              <div>
                <div className="text-[10px] uppercase text-slate-500">Período</div>
                <div className="font-mono text-sm">{lastCalc.calc_start} → {lastCalc.calc_end}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Principal</div>
                <div className="font-mono text-sm">
                  {(Number(lastCalc.total_cents)/100).toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-500">Total atualizado</div>
                <div className="font-mono text-sm">
                  {(Number(lastCalc.total_updated_cents || lastCalc.total_cents)/100).toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                </div>
              </div>
              {Number(lastCalc.total_deducoes_cents) > 0 && (
                <div data-testid="liq-detail-deducoes">
                  <div className="text-[10px] uppercase text-rose-700">Deduções (INSS/IR)</div>
                  <div className="font-mono text-sm text-rose-700">
                    − {(Number(lastCalc.total_deducoes_cents)/100).toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                  </div>
                </div>
              )}
              {Number(lastCalc.total_liquido_cents) > 0 && (
                <div data-testid="liq-detail-liquido">
                  <div className="text-[10px] uppercase text-emerald-800">Líquido a receber</div>
                  <div className="font-mono text-lg font-bold text-emerald-800">
                    {(Number(lastCalc.total_liquido_cents)/100).toLocaleString("pt-BR",{style:"currency",currency:"BRL"})}
                  </div>
                </div>
              )}
              <div>
                <div className="text-[10px] uppercase text-slate-500">Motor</div>
                <div className="font-mono text-xs text-slate-500">{lastCalc.engine_version}</div>
              </div>
            </div>
          </div>
        )}

        {/* Impugnação (Fase 17) */}
        {lastCalc && <ImpugnacaoCard caseId={caseData.id} calcId={lastCalc.id} />}

        {/* Ações principais */}
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => nav(`/calculos/liquidacao-trabalhista/${caseData.id}/titulo`)}
            data-testid="liq-detail-goto-title"
            className="h-10 px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-100 text-slate-800 text-sm font-medium inline-flex items-center gap-2">
            <FileText className="w-4 h-4"/> Título executivo
          </button>
          <button onClick={() => nav(`/calculos/liquidacao-trabalhista/${caseData.id}/parametros`)}
            data-testid="liq-detail-goto-params"
            className="h-10 px-4 rounded-xl bg-white border border-slate-300 hover:bg-slate-100 text-slate-800 text-sm font-medium inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4"/> Parâmetros
          </button>
          <button onClick={() => nav(`/calculos/liquidacao-trabalhista/${caseData.id}/calculo`)}
            data-testid="liq-detail-goto-calc"
            className="h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4"/> Cálculo
          </button>
        </div>

        {/* Upload zone */}
        <div
          onDrop={onDrop}
          onDragEnter={onDragEnter}
          onDragOver={(e) => e.preventDefault()}
          onDragLeave={onDragLeave}
          className={`mt-6 rounded-2xl border-2 border-dashed p-8 text-center transition-all ${
            dragOver ? "border-slate-900 bg-slate-100"
                     : "border-slate-300 bg-white hover:border-slate-400"}`}
          data-testid="liq-detail-dropzone"
        >
          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2"/>
          <div className="font-medium text-slate-800">
            Arraste os documentos do processo aqui
          </div>
          <div className="text-xs text-slate-500 mt-1">
            PDF, DOC/DOCX, XLS/XLSX, CSV, TXT, imagens, XML e PJC — até 50 MB por arquivo.
            Arquivos idênticos são ignorados automaticamente (SHA-256).
          </div>
          <label className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium cursor-pointer"
                 data-testid="liq-detail-upload-btn">
            <Upload className="w-4 h-4"/>
            {uploading ? "Enviando…" : "Selecionar arquivos"}
            <input type="file" multiple className="hidden"
                   onChange={(e) => upload(e.target.files)}
                   disabled={uploading}
                   data-testid="liq-detail-upload-input"/>
          </label>
        </div>

        {/* Lista de documentos */}
        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[11px] uppercase tracking-widest text-slate-500">
              Documentos enviados
            </div>
            <button onClick={load} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="liq-detail-reload">
              <RefreshCcw className="w-3 h-3"/> Recarregar
            </button>
          </div>
          {docs.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500"
                 data-testid="liq-detail-empty-docs">
              <FolderOpen className="w-8 h-8 text-slate-300 mx-auto mb-2"/>
              Nenhum documento enviado ainda.
            </div>
          ) : (
            <div className="space-y-2" data-testid="liq-detail-docs">
              {docs.map(d => (
                <div key={d.id}
                  className={`rounded-xl border ${d.quarantined ? "border-rose-200 bg-rose-50"
                    : d.purged_at ? "border-slate-200 bg-slate-50 opacity-70"
                    : "border-slate-200 bg-white"} p-3 flex items-start gap-3`}
                  data-testid={`liq-doc-${d.id}`}>
                  <div className="w-9 h-9 rounded-lg bg-slate-100 inline-flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-slate-500"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                      {d.original_filename}
                      {d.category && d.category !== "OTHER" && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide font-semibold ${
                            d.category_source === "USER" ? "bg-slate-900 text-white"
                          : d.category_source === "AI" ? "bg-blue-100 text-blue-800"
                          : "bg-slate-100 text-slate-700"}`}
                          title={d.category_reason || ""}
                          data-testid={`liq-doc-cat-badge-${d.id}`}>
                          {d.category_source === "AI" && <Sparkles className="inline w-2.5 h-2.5 mr-0.5"/>}
                          {d.category}
                          {d.category_confidence ? ` · ${Math.round(d.category_confidence*100)}%` : ""}
                        </span>
                      )}
                      {d.purged_at && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase font-semibold">
                          apagado do servidor
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500 flex flex-wrap gap-2">
                      <span>{formatBytes(d.size_bytes)}</span>
                      <span>· {d.mime}</span>
                      <span>· {new Date(d.uploaded_at).toLocaleString("pt-BR")}</span>
                      <span className="inline-flex items-center gap-1">
                        {d.ocr_state === "DONE" ? <CheckCircle2 className="w-3 h-3 text-emerald-600"/>
                          : d.ocr_state === "PENDING" ? <Clock className="w-3 h-3 text-amber-600"/>
                          : d.ocr_state === "FAILED" ? <AlertTriangle className="w-3 h-3 text-rose-600"/>
                          : <Clock className="w-3 h-3"/>}
                        OCR: {d.ocr_state}
                      </span>
                    </div>
                  </div>
                  <select
                    value={d.category || "OTHER"}
                    onChange={(e) => reclassify(d.id, e.target.value)}
                    className="text-xs bg-slate-50 border border-slate-200 rounded-md px-2 py-1"
                    data-testid={`liq-doc-cat-${d.id}`}
                  >
                    {CATEGORIES.map(([k, l]) => (
                      <option key={k} value={k}>{l}</option>
                    ))}
                  </select>
                  {!d.purged_at && (
                    <button onClick={() => downloadToVault(d)}
                      className="text-xs text-slate-700 hover:text-slate-900 h-8 px-2 inline-flex items-center gap-1 rounded-md bg-slate-100 hover:bg-slate-200"
                      title="Baixar para o cofre local do cliente e apagar do servidor"
                      data-testid={`liq-doc-vault-${d.id}`}>
                      <DownloadCloud className="w-3.5 h-3.5"/> Salvar local
                    </button>
                  )}
                  <button onClick={() => remove(d.id)}
                    className="text-xs text-rose-600 hover:text-rose-800 h-8 px-2 inline-flex items-center gap-1 rounded-md hover:bg-rose-50"
                    data-testid={`liq-doc-remove-${d.id}`}>
                    <Trash2 className="w-3.5 h-3.5"/>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Meta editable */}
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3">
            Dados do processo (autosave)
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <MetaField label="Reclamante" value={caseData.claimant?.name || ""}
                       onChange={v => patch({ claimant: { ...(caseData.claimant||{}), name: v }})}
                       testid="liq-detail-claimant"/>
            <MetaField label="Reclamada" value={caseData.defendant?.name || ""}
                       onChange={v => patch({ defendant: { ...(caseData.defendant||{}), name: v }})}
                       testid="liq-detail-defendant"/>
            <MetaField label="Vara" value={caseData.court || ""}
                       onChange={v => patch({ court: v })}
                       testid="liq-detail-court"/>
            <MetaField label="TRT" value={caseData.regional_court || ""}
                       onChange={v => patch({ regional_court: v })}
                       testid="liq-detail-regional"/>
            <MetaField label="Admissão" type="date" value={caseData.employment_start || ""}
                       onChange={v => patch({ employment_start: v })}
                       testid="liq-detail-empstart"/>
            <MetaField label="Rescisão" type="date" value={caseData.employment_end || ""}
                       onChange={v => patch({ employment_end: v })}
                       testid="liq-detail-empend"/>
          </div>
        </div>

        {/* Aviso Fase 0 */}
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
             data-testid="liq-detail-phase0-notice">
          <b>Fase 0 concluída.</b> Você já pode cadastrar processos e enviar documentos.
          As próximas fases (OCR, consolidação do título, extração de parâmetros,
          motor de cálculo, auditoria e impugnação) serão liberadas
          progressivamente — sem quebrar o que já foi feito.
        </div>
      </div>
    </div>
  );
}

function WhatsAppShareButton({ token, totalCents, processNumber, claimantName }) {
  const [open, setOpen] = useState(false);
  const shareUrl = `${window.location.origin}/api/public/liq/calc/${token}/share.html`;
  const spaUrl = `${window.location.origin}/verificacao/calculo/${token}`;

  const brl = (Number(totalCents || 0) / 100)
    .toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const msg =
    `📊 *Memória de cálculo — Hermes Calc*\n\n` +
    (processNumber ? `Processo: ${processNumber}\n` : "") +
    (claimantName ? `Reclamante: ${claimantName}\n` : "") +
    `Total atualizado: *${brl}*\n\n` +
    `Confira a memória detalhada e o selo de auditoria:\n${shareUrl}`;

  const wa = `https://wa.me/?text=${encodeURIComponent(msg)}`;

  const emailSubject = processNumber
    ? `Memória de cálculo — ${processNumber}`
    : "Memória de cálculo — Hermes Calc";
  const emailBody =
    `Olá,\n\n` +
    `Segue o selo de auditoria da memória de cálculo elaborada pelo Hermes Calc.\n\n` +
    (processNumber ? `Processo: ${processNumber}\n` : "") +
    (claimantName ? `Reclamante: ${claimantName}\n` : "") +
    `Total atualizado: ${brl}\n\n` +
    `Para conferir a memória detalhada, valores por competência e regras aplicadas, ` +
    `acesse o link abaixo (não requer login):\n${shareUrl}\n\n` +
    `O selo é emitido por motor determinístico auditável — mesma entrada, mesma saída, ` +
    `sempre. Qualquer dúvida, estou à disposição.\n\n` +
    `Atenciosamente.`;
  const mailto = `mailto:?subject=${encodeURIComponent(emailSubject)}` +
                  `&body=${encodeURIComponent(emailBody)}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copiado — o preview aparecerá ao colar no WhatsApp");
    } catch {
      toast.error("Não foi possível copiar — copie manualmente");
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="text-xs text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-emerald-50 border border-emerald-200 hover:bg-emerald-100"
        data-testid="liq-detail-share-whatsapp"
        title="Compartilhar selo por WhatsApp"
      >
        <Share2 className="w-3.5 h-3.5"/> Compartilhar
      </button>
      {open && (
        <div
          className="absolute right-0 top-10 z-20 w-72 rounded-xl border border-slate-200 bg-white shadow-xl p-3"
          data-testid="liq-detail-share-menu"
        >
          <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">
            Enviar selo de auditoria
          </div>
          <a
            href={wa}
            target="_blank"
            rel="noopener"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-semibold"
            data-testid="liq-detail-share-wa-open"
          >
            <span className="w-5 h-5 rounded-full bg-white/20 grid place-items-center text-[10px] font-bold">W</span>
            Abrir no WhatsApp
          </a>
          <a
            href={mailto}
            onClick={() => setOpen(false)}
            className="mt-2 flex items-center gap-2 h-10 px-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-semibold"
            data-testid="liq-detail-share-email"
          >
            <Mail className="w-4 h-4"/> Enviar por e-mail
          </a>
          <button
            type="button"
            onClick={copyLink}
            className="mt-2 w-full flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-sm text-slate-800"
            data-testid="liq-detail-share-copy"
          >
            <Copy className="w-4 h-4"/> Copiar link
          </button>
          <div className="mt-3 text-[11px] text-slate-500 leading-relaxed">
            O link gera um <em>preview automático</em> com processo, reclamante e total.
            Ao clicar, o destinatário vê a memória em <span className="font-mono text-emerald-700">{spaUrl.replace(window.location.origin, "")}</span>.
          </div>
        </div>
      )}
    </div>
  );
}


function Kpi({ label, value, tone = "", testid }) {
  const toneCls =
    tone === "ok"   ? "border-emerald-200 bg-emerald-50 text-emerald-900" :
    tone === "warn" ? "border-amber-200 bg-amber-50 text-amber-900" :
                       "border-slate-200 bg-white text-slate-800";
  return (
    <div className={`rounded-xl border p-3 ${toneCls}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-2xl font-semibold">{value}</div>
    </div>
  );
}

function MetaField({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        className="mt-1 w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm"
        data-testid={testid}/>
    </label>
  );
}

function formatBytes(b) {
  if (!b || b < 1024) return `${b || 0} B`;
  if (b < 1024 * 1024) return `${(b/1024).toFixed(1)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
}


function ImpugnacaoCard({ caseId, calcId }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const brl = (c) => (Number(c || 0) / 100)
    .toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get(`/liq/cases/${caseId}/impugnacoes`);
      setHistory(r.data.rows || []);
    } catch { /* silent */ }
  }, [caseId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const submit = async () => {
    let opp;
    try {
      opp = JSON.parse(text);
    } catch {
      toast.error("JSON inválido — cole o cálculo adversário completo");
      return;
    }
    if (!opp || !Array.isArray(opp.verbas)) {
      toast.error("JSON precisa conter { verbas: [...], total_updated_cents }");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        `/liq/cases/${caseId}/impugnacao/analyze`,
        { calc_id: calcId, opponent_calculation: opp, threshold_pct: 0.05 }
      );
      setResult(r.data);
      await loadHistory();
      if (!r.data.diff.diverges) {
        toast.success("Cálculos praticamente equivalentes (< 5% de divergência)");
      } else {
        toast.success(`Divergência de ${(r.data.diff.total_pct_diff*100).toFixed(1)}% detectada`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha na análise");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-rose-200 bg-white p-5"
          data-testid="impugnacao-card">
      <div className="flex items-center gap-2 mb-2">
        <AlertTriangle className="w-4 h-4 text-rose-700"/>
        <h3 className="font-semibold text-slate-900">Impugnação ao cálculo adversário</h3>
        <button
          type="button"
          onClick={() => setOpen(o => !o)}
          className="ml-auto text-xs h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50"
          data-testid="impugnacao-toggle"
        >
          {open ? "Recolher" : "Analisar cálculo do polo contrário"}
        </button>
      </div>
      {open && (
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            Cole o JSON do cálculo apresentado pela outra parte (mesmo formato exportado
            pelo Hermes). O motor faz o diff verba-a-verba; se a divergência total
            superar <strong>5%</strong>, uma minuta de impugnação é gerada com IA.
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            rows={6}
            placeholder='{"verbas": [{"code":"horas_extras","label":"HE","total_cents":100000}], "total_updated_cents": 100000}'
            className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs font-mono"
            data-testid="impugnacao-json-input"
          />
          <button
            onClick={submit}
            disabled={busy || !text.trim()}
            className="h-10 px-4 rounded-xl bg-rose-700 hover:bg-rose-600 disabled:opacity-50 text-white text-sm font-semibold inline-flex items-center gap-2"
            data-testid="impugnacao-submit"
          >
            {busy ? "Analisando..." : "Analisar divergências"}
          </button>

          {result && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2"
                  data-testid="impugnacao-result">
              <div className="text-xs text-slate-500 uppercase">Resultado</div>
              <div className="text-sm">
                Meu total: <strong className="font-mono">{brl(result.diff.total_mine_cents)}</strong> ·
                Contrário: <strong className="font-mono">{brl(result.diff.total_theirs_cents)}</strong> ·
                Δ: <strong className={result.diff.diverges ? "text-rose-700" : "text-emerald-700"}>
                  {(result.diff.total_pct_diff*100).toFixed(2)}%
                </strong>
              </div>
              {result.diff.significant_items?.length > 0 && (
                <ul className="text-xs text-slate-700 list-disc pl-5 space-y-1">
                  {result.diff.significant_items.map(d => (
                    <li key={d.code}>
                      <strong>{d.label}</strong>: meu {brl(d.mine_cents)} / contrário {brl(d.theirs_cents)}
                      ({(d.pct_diff*100).toFixed(1)}%)
                      {d.only_in_mine && <em className="text-rose-700"> — omitido pelo contrário</em>}
                      {d.only_in_theirs && <em className="text-amber-700"> — sem amparo no título</em>}
                    </li>
                  ))}
                </ul>
              )}
              {result.peca?.texto && (
                <div className="mt-2">
                  <div className="text-xs uppercase text-slate-500 mb-1">Minuta da peça de impugnação</div>
                  <pre className="text-xs whitespace-pre-wrap bg-white border border-slate-200 rounded p-3 max-h-72 overflow-auto"
                        data-testid="impugnacao-peca">{result.peca.texto}</pre>
                </div>
              )}
              {result.peca?.reason && !result.peca.texto && (
                <div className="text-xs text-slate-500 italic">{result.peca.reason}</div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Histórico de impugnações */}
      {history.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-100" data-testid="impugnacao-history">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-slate-600"/>
            <h4 className="text-sm font-semibold text-slate-900">
              Histórico de impugnações ({history.length})
            </h4>
          </div>
          <ul className="space-y-2">
            {history.map(h => {
              const d = h.diff || {};
              const generated = h.generated_at ? new Date(h.generated_at).toLocaleString("pt-BR") : "";
              const pct = ((d.total_pct_diff || 0) * 100).toFixed(1);
              const diverges = d.diverges;
              return (
                <li key={h.id}
                    className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-slate-500">{generated}</span>
                      <span className={"px-2 py-0.5 rounded-full text-[10px] font-semibold " +
                        (diverges ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800")}>
                        {diverges ? `Δ ${pct}%` : `equivalente`}
                      </span>
                      {h.peca?.texto && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-100 text-sky-800">
                          minuta pronta
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Meu: <span className="font-mono">{brl(d.total_mine_cents)}</span> ·
                      Contrário: <span className="font-mono">{brl(d.total_theirs_cents)}</span> ·
                      {(d.significant_count || 0)} verba(s) significativa(s)
                    </div>
                  </div>
                  <a
                    href={`${API}/liq/impugnacoes/${h.id}/docx`}
                    className="text-xs h-8 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center gap-1"
                    data-testid={`impugnacao-docx-${h.id}`}
                  >
                    <DownloadCloud className="w-3.5 h-3.5"/> .docx
                  </a>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
